import React, { useRef, useState } from 'react';
import { X } from 'react-feather';
import InlineSelect from '../InlineTable/InlineSelect';

/** A titled group of fields inside a wizard step. */
export function EtlSection({ title, description, actions, children }) {
  return (
    <section className="ep-section">
      {(title || description || actions) ? (
        <div className="ep-section-head">
          {title ? <h3>{title}</h3> : null}
          {description ? <p>{description}</p> : null}
          {actions ? <div className="ep-body-head-actions">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** Responsive field grid. `single` forces one column (long inputs, editors). */
export function EtlGrid({ single = false, children }) {
  return <div className={`ep-grid${single ? ' ep-one' : ''}`}>{children}</div>;
}

/**
 * EtlField — label + control + help, the same on every ETL form.
 * Pass `span` to make the field take the full grid width.
 */
export function EtlField({ label, required, help, error, span, htmlFor, children }) {
  return (
    <div className={`cp-field${span ? ' ep-span-2' : ''}`}>
      {label ? (
        <label className="cp-label" htmlFor={htmlFor}>
          {label}
          {required ? <span className="ep-req" aria-hidden="true">*</span> : null}
        </label>
      ) : null}
      {children}
      {error ? <div className="cp-help" style={{ color: 'var(--color-error-ink)' }}>{error}</div> : null}
      {!error && help ? <div className="cp-help">{help}</div> : null}
    </div>
  );
}

/** A value the user can see but not change (current organization, resolved path…). */
export function EtlReadonly({ children }) {
  return <div className="ep-readonly">{children}</div>;
}

/**
 * ChipInput — keyword lists (match keywords, exclude keywords, recipients).
 * Enter or comma commits; Backspace on an empty box removes the last chip.
 */
export function ChipInput({ value = [], onChange, placeholder = 'Type and press Enter…', exclude = false, disabled, ariaLabel }) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  const commit = (raw) => {
    const next = String(raw || '').trim();
    if (!next) return;
    if (!value.includes(next)) onChange([...value, next]);
    setDraft('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(draft);
    } else if (e.key === 'Backspace' && !draft && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="ep-chips" onClick={() => inputRef.current?.focus()}>
      {value.map((chip) => (
        <span key={chip} className={`ep-chip-tag${exclude ? ' ep-exclude' : ''}`}>
          {chip}
          {!disabled ? (
            <button type="button" onClick={(e) => { e.stopPropagation(); onChange(value.filter((c) => c !== chip)); }} aria-label={`Remove ${chip}`}>
              <X size={11} />
            </button>
          ) : null}
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => commit(draft)}
        placeholder={value.length ? '' : placeholder}
        aria-label={ariaLabel || placeholder}
      />
    </div>
  );
}

/**
 * ChoiceCards — a radio group that reads as cards. Used wherever the user is
 * choosing a KIND of thing (source type, extraction method, match mode);
 * a bare <select> hides the options behind a click and gives no room to say
 * what each one means.
 *
 * options [{ value, title, sub?, icon?: Component, disabled? }]
 */
export function ChoiceCards({ options = [], value, onChange, ariaLabel }) {
  return (
    <div className="ep-choices" role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt) => {
        const Icon = opt.icon;
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={opt.disabled}
            className={`ep-choice${selected ? ' ep-selected' : ''}`}
            onClick={() => onChange(opt.value)}
          >
            {Icon ? <span className="ep-choice-icon"><Icon size={16} /></span> : null}
            <span className="ep-choice-text">
              <span className="ep-choice-title">{opt.title}</span>
              {opt.sub ? <span className="ep-choice-sub">{opt.sub}</span> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * FilterSelect — an InlineSelect sized for a toolbar rather than a table cell.
 *
 * The kit select is built to fill a column (width:100%, border only on hover)
 * so it can't be dropped straight into a flex toolbar: it stretches, and at
 * rest it looks like plain text. This gives it a fixed box and keeps the
 * border on, which is what a filter control needs.
 */
export function FilterSelect({ width = 172, ...props }) {
  return (
    <div style={{ width, flex: 'none' }}>
      <InlineSelect rowActive {...props} />
    </div>
  );
}
